import React from "react";
import { Alert, Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import SectionHeader from "../components/common/SectionHeader";
import CommonDataGrid from "../components/grid/CommonDataGrid";
import { recommendationsApi } from "../services/recommendationsApi";
import { marketDataApi } from "../services/marketDataApi";

const CaptinAiPage = () => {
  const { data: recommendations = [], isLoading: recommendationsLoading, error } =
    recommendationsApi.endpoints.getAllLatestRecommendations.useQuery();
  const { data: marketAssets = [], isLoading: assetsLoading } =
    marketDataApi.endpoints.getAllMarketAssets.useQuery();

  const assetLookup = React.useMemo(
    () => marketAssets.reduce((acc, asset) => ({ ...acc, [asset.marketAssetId]: asset }), {}),
    [marketAssets]
  );

  const rows = React.useMemo(
    () =>
      recommendations.map((rec) => ({
        ...rec,
        symbol: assetLookup[rec.marketAssetId]?.symbol || `#${rec.marketAssetId}`,
        name: assetLookup[rec.marketAssetId]?.name || "Unknown Asset",
      })),
    [recommendations, assetLookup]
  );

  const columns = React.useMemo(
    () => [
      { field: "symbol", headerName: "Symbol", valueGetter: (row) => row.symbol },
      { field: "name", headerName: "Asset", valueGetter: (row) => row.name },
      {
        field: "overallRecommendation",
        headerName: "Recommendation",
        valueGetter: (row) => row.overallRecommendation?.recommendationLevel || "—",
        renderCell: (row) => (
          <Chip size="small" color="primary" label={row.overallRecommendation?.recommendationLevel || "—"} />
        ),
      },
      { field: "trend", headerName: "Trend", valueGetter: (row) => row.trendAnalysis?.trend || "—" },
      { field: "risk", headerName: "Risk", valueGetter: (row) => row.riskAssessment?.level || "—" },
      { field: "sentiment", headerName: "Sentiment", valueGetter: (row) => row.sentimentAnalysis?.level || "—" },
      {
        field: "dateGenerated",
        headerName: "Generated",
        valueGetter: (row) => row.dateGenerated,
        valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
      },
    ],
    []
  );

  if (recommendationsLoading || assetsLoading) return <LinearProgress />;
  if (error) return <Alert severity="error">Failed to load AI recommendations.</Alert>;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <SectionHeader variant="h4" component="h1" gutterBottom sx={{ pb: 0.75 }}>
        Captin AI
      </SectionHeader>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        AI-driven recommendations and market intelligence for your tracked assets.
      </Typography>
      <CommonDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.aiAssetRecommendationId}
        initialSortField="dateGenerated"
        initialSortDirection="desc"
        filterPlaceholder="Filter AI insights by symbol, recommendation, trend..."
      />
    </Box>
  );
};

export default CaptinAiPage;
